import { useState } from 'react';
import { Loader2, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

export default function ProfilePage() {
  const { user, logout, refreshUser, authFetch } = useAuth();
  const { showToast } = useToast();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!user) return null;

  const userInitials = user.full_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??';

  const hasChanges = fullName.trim() !== user.full_name;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!fullName.trim()) {
      setError('Name cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch('/auth/me', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ full_name: fullName.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update profile');
      }

      await refreshUser();
      showToast('Profile updated', 'success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="bg-white rounded-lg border border-softgrey p-8 text-center">
        <div className="w-20 h-20 rounded-full bg-brand flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
          {userInitials}
        </div>
        <h2 className="text-xl font-semibold text-graphite">{user.full_name}</h2>
        <p className="text-sm text-coolslate">{user.email}</p>
        <p className="text-xs text-coolslate mt-1 capitalize">{user.roles?.join(', ')}</p>
      </div>

      <div className="bg-white rounded-lg border border-softgrey p-6">
        <h3 className="text-base font-semibold text-graphite mb-4">Edit Profile</h3>

        {error && (
          <div className="bg-state-error/10 border border-state-error/30 text-state-error px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-graphite mb-1.5">
              Full Name
            </label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-4 py-2.5 border border-softgrey rounded-lg text-graphite focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-graphite mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={user.email}
              disabled
              className="w-full px-4 py-2.5 border border-softgrey rounded-lg text-coolslate bg-offwhite cursor-not-allowed"
            />
          </div>

          <button
            type="submit"
            disabled={saving || !hasChanges}
            className="w-full py-2.5 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-lg border border-softgrey p-6">
        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-state-error border border-state-error/30 rounded-lg font-medium hover:bg-state-error/5 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </div>
  );
}
